'use client';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { useTranslations } from 'next-intl';
import { Icon } from '@/lib/ui-kit/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { cn } from '@/lib/ui-kit/utils';
import { BUTTON_S_CLASS, LABEL_CLASS, TOOLTIP_CONTENT_CLASS, Z_OVERLAY_MAX } from '../constants';
import type { PriceAlertRepeat } from '../types';

interface RepeatFieldProps {
  value: PriceAlertRepeat;
  onChange: (value: PriceAlertRepeat) => void;
}

const OPTIONS: PriceAlertRepeat[] = ['ONCE', 'PER_TRIGGER'];

export function RepeatField({ value, onChange }: RepeatFieldProps) {
  const tf = useTranslations('priceAlerts.form');

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1">
        <span className={LABEL_CLASS}>{tf('repeat')}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={tf('repeatTooltip')}
              className="flex shrink-0 cursor-pointer items-center justify-center text-v2-text-secondary outline-none transition-colors hover:text-v2-text-primary"
            >
              <Icon name="Attention" size={12} />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            showArrow={false}
            style={{ textWrap: 'wrap', zIndex: Z_OVERLAY_MAX }}
            className={TOOLTIP_CONTENT_CLASS}
          >
            {tf('repeatTooltip')}
          </TooltipContent>
        </Tooltip>
      </div>
      <RadioGroupPrimitive.Root
        value={value}
        onValueChange={(v) => onChange(v as PriceAlertRepeat)}
        className="flex h-10 items-center gap-1 rounded-[10px] bg-v2-btn-secondary p-1"
        aria-label={tf('repeat')}
      >
        {OPTIONS.map((opt) => {
          const active = value === opt;
          return (
            <RadioGroupPrimitive.Item
              key={opt}
              value={opt}
              className={cn(
                'flex flex-1 cursor-pointer items-center justify-center self-stretch rounded-[8px] px-2 outline-none transition-colors focus-visible:ring-1 focus-visible:ring-v2-border',
                BUTTON_S_CLASS,
                active
                  ? 'bg-v2-surface-primary text-v2-text-primary shadow-sm dark:bg-v2-surface-tertiary dark:shadow-none'
                  : 'bg-transparent text-v2-text-secondary hover:text-v2-text-primary',
              )}
            >
              {opt === 'ONCE' ? tf('repeatOptions.once') : tf('repeatOptions.every')}
            </RadioGroupPrimitive.Item>
          );
        })}
      </RadioGroupPrimitive.Root>
    </div>
  );
}
